
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import { ParamsCommande } from '@/model/params-commande';
import { FicheArticle } from '@/model/fiche-article';
import { ModeCamion } from '@/model/mode-camion';

import { parseISO, startOfDay, isBefore, addDays, isWithinInterval } from 'date-fns';
import { Adresse } from '@/model/adresse';
import { RouteName } from '@/router/routes';

import { TiersApi } from '@/api/tiers';

import { required, withMessage, isDate, and, custom, numeric, gt, _if } from 'form-validation-manager';
import { ArticleApi } from '@/api/article';
import { ItemPanier } from '@/store/modules/panier';
import { cloneDeep, find } from 'lodash';
import { ModeReglement } from '@/model/mode-reglement';
import { DomiciliationBancaire } from '@/model/domiciliations-bancaires';
import { Conditionnement } from '@/model/conditionnement';
import { getQuantiteUnite } from '@/utils/conditionnement';
import { BrsTiers } from '@/model/brs-tiers';
import { LigneCommande } from '@/model/commande/ligne-commande';
import { Commande } from '@/model/commande/commande';
import { CommandeApi } from '@/api/commande';
import { ArticleLieuDepart } from '@/model/article-lieu-depart';
import { ReglementCommande } from '@/model/commande/reglement-commande';

const isNotPastValidation = (value: Date) => isBefore(value, startOfDay(Date.now())) ? 'La date ne peut être dans le passé' : false;

export default Vue.extend({
  name: 'AjoutAuPanier',
  props: {
    idArticle: {
      type: Number,
      required: true
    },
    idLignePanier: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    loading: true,
    loadingRechPrix: true,
    loadingDateEcheance: false,
    ficheArticle: null as FicheArticle,
    showCommentaireDialog: false,
    paramsCommande: null as ParamsCommande,
    dateEcheanceModifiable: false as boolean,
    form: {
      quantite: null as number,
      livraison: {
        echeanceLivraison: null as Date,
        adresse: null as Adresse,
        transport: null,
        commentaire: null
      },
      paiement: {
        echeancePaiement: null as Date,
        reglement: null as ModeReglement,
        domiciliationBancaire: null as DomiciliationBancaire,
        tarif: null as number,
        tarifTTC: null as number
      }
    }
  }),
  validations: {
    form: {
      quantite: and(
        withMessage(and(required(), numeric(), gt(0)), 'Veuillez renseigner une quantité positive'),
        custom(function () {
          if (this.ficheArticle) {
            if (this.quantiteTonne > this.ficheArticle.stock.valeur) {
              return 'Veuillez renseigner une valeur inférieure à ' + this.ficheArticle.stock.valeur + ' t';
            }
            return false;
          }
        }),
        custom(function (value) {
          if (value % 1 > 0) {
            return 'Veuillez renseigner une quantité entière';
          }
          return false;
        })
      ),
      livraison: {
        echeanceLivraison: and(
          withMessage(isDate(), 'La date de livraison est obligatoire.'),
          custom(isNotPastValidation)
        )
      },
      paiement: {
        reglement: withMessage(required(), 'Veuillez sélectionner un mode de paiement'),
        domiciliationBancaire: _if(function (this: any) {
          return this.form.paiement.reglement.typeEcheance === 'PRELEVEMENT';
        }, withMessage(required(), 'Veuillez sélectionner une domiciliation bancaire')),
        tarif: withMessage(required(), 'Modalités de règlement non valides')
      }
    }
  },
  computed: {
    ...mapGetters({
      articlesLieuxDepart: 'articlesLieuxDepart/liste',
      infoTiers: 'tiers/infosTiers',
      itemsOfPanier: 'panier/items',
      preferencesUtilisateur: 'preferenceUtilisateur/preferenceUtilisateur'
    }),
    montant (): number {
      return this.form.paiement.tarif * this.quantiteTonne;
    },
    montantTTC (): number {
      return this.form.paiement.tarifTTC * this.quantiteTonne;
    },
    conditionnement (): Conditionnement {
      return this.paramsCommande.conditionnements[0];
    },
    quantiteTonne (): number {
      return getQuantiteUnite(this.form.quantite, this.conditionnement, 't');
    },
    modesReglement (): ModeReglement[] {
      return this.paramsCommande.modesReglement || [];
    },
    domiciliationsBancaires (): DomiciliationBancaire[] {
      return this.paramsCommande.domiciliationsBancaires || [];
    },
    modeEdition (): boolean {
      return this.idLignePanier !== null;
    },
    adresses (): Adresse[] {
      return (this.infoTiers as BrsTiers).adresses;
    },
    modesCamion (): ModeCamion[] {
      return this.paramsCommande.modeCamions;
    }

  },
  watch: {
    'form.paiement.reglement' (value) {
      if (value) this.changeReglement();
    },
    'form.paiement.echeancePaiement' (value) {
      if (value) this.changeEcheancePaiement();
    }
  },
  created () {
    Promise.all([
      ArticleApi.getFicheArticle(this.idArticle, null).then((ficheArticle: FicheArticle) => { this.ficheArticle = ficheArticle; }),
      this.loadParamsCommandeArticle(this.idArticle).then((params) => { this.paramsCommande = params; }),
      this.articlesLieuxDepart ? Promise.resolve(this.articlesLieuxDepart) : this.loadArticlesLieuxDepart()
    ]).then(() => {
      if (this.ficheArticle.prixAppel === undefined) {
        const textHtml = '<p>Attention, la quantité en stock n\'est plus suffisante en stock depuis votre dernière saisie </p>';

        this.$dialog.info({
          title: 'Attention',
          text: textHtml,
          actions: {
            0: {
              text: 'J\'ai compris',
              flat: false,
              outlined: true,
              color: 'info'
            }
          }
        });

        this.clearCacheArticles();
        this.$router.push({ name: RouteName.MAIN_DASHBOARD });
      } else {
        this.initForm();
        this.calculPrix();
      }
    }).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    ...mapActions({
      loadParamsCommandeArticle: 'article/loadParamsCommandeArticle',
      ajouterAuPanier: 'panier/ajouterItemPanier',
      updateItemPanier: 'panier/modifierItemPanier',
      loadArticlesLieuxDepart: 'articlesLieuxDepart/loadListe',
      clearCacheArticles: 'article/clearCache'
    }),
    getLignePanier () {
      if (this.modeEdition) {
        return find(this.itemsOfPanier as ItemPanier[], { idLigne: this.idLignePanier });
      } else {
        return null;
      }
    },
    capitalize (str: string) {
      return str?.toLowerCase().replace(/(\W|^)\w/g, c => c.toUpperCase());
    },
    initForm () {
      if (this.modeEdition) {
        const itemPanier = this.getLignePanier();
        if (!itemPanier) {
          this.$router.push({ name: RouteName.MAIN_DASHBOARD });
          return;
        }

        this.form.quantite = itemPanier.quantite;

        this.form.livraison.echeanceLivraison = itemPanier.echeanceLivraison;
        this.form.livraison.adresse = find(this.adresses, { numAdr: itemPanier.adresse.numAdr });

        this.form.paiement.echeancePaiement = itemPanier.echeancePaiement;
        this.form.paiement.reglement = find(this.modesReglement, { codeReglement: itemPanier.modeReglement.codeReglement });
        this.form.paiement.domiciliationBancaire = find(this.domiciliationsBancaires, { numDomiciliationBancaire: itemPanier.domiciliationBancaire.numDomiciliationBancaire });
      } else {
        this.form.quantite = 1;

        this.form.livraison.echeanceLivraison = this.ficheArticle.prixAppel.dateLivraison;
        this.form.livraison.adresse = this.preferencesUtilisateur.adresseLivraisonFavorite
          ? this.infoTiers.adresses.find((a: Adresse) => a.numAdr === this.preferencesUtilisateur.adresseLivraisonFavorite)
          : (this.adresses).find(a => a.typeAdr === 'F');

        this.form.paiement.echeancePaiement = this.ficheArticle.prixAppel.datepaiement;
        this.form.paiement.reglement = this.modesReglement[0];
        if (this.paramsCommande.domiciliationsBancaires.length > 0) {
          this.form.paiement.domiciliationBancaire = this.paramsCommande.domiciliationsBancaires[0];
        }
      }
    },
    allowedDate (date: string) {
      const _date = parseISO(date);
      return !(isBefore(_date, startOfDay(Date.now())));
    },
    buildHtmlAdresse (adresse: any) {
      const p1 = [adresse.ligneAdresse1 || '', adresse.ligneAdresse2 || '', adresse.ligneAdresse3 || '']
        .map(i => i.trim())
        .filter(i => !!i)
        .join(', ');

      const p2 = adresse.codePostal + ' <wbr>' + adresse.ville;

      return `<span class="line">
                <span class="mx-1">*</span>
                <span>${p1}</span>
                <span class="mx-1">-</span>
                <span>${p2}</span>
              </span>`;
    },
    cancel () {
      this.$dialog.confirm({
        title: 'Confirmation',
        text: 'Toute modification sera perdue. Voulez-vous continuer ?',
        actions: {
          false: {
            text: 'Annuler',
            flat: false,
            outlined: true,
            color: 'grey'
          },
          true: {
            text: 'Continuer',
            color: 'primary',
            flat: false
          }
        }
      }).then(confirm => {
        if (confirm) {
          this.$router.push({ name: RouteName.MAIN_PANIER });
        }
      });
    },
    save () {
      (this.$refs.form as any).validate();
      if (this.$fvm.$valid) {
        const itemPanier = this.modeEdition ? cloneDeep(this.getLignePanier()) : new ItemPanier();
        itemPanier.articleLieuDepart = this.articlesLieuxDepart.find((ald: ArticleLieuDepart) => ald.article.id === this.idArticle);
        itemPanier.quantite = this.form.quantite;
        itemPanier.conditionnement = this.conditionnement;
        itemPanier.echeanceLivraison = this.form.livraison.echeanceLivraison;
        itemPanier.echeancePaiement = this.form.paiement.echeancePaiement;
        itemPanier.modeReglement = this.form.paiement.reglement;
        itemPanier.adresse = this.form.livraison.adresse;
        itemPanier.domiciliationBancaire = this.form.paiement.domiciliationBancaire;
        itemPanier.prixUnitaireHT = this.form.paiement.tarif;
        itemPanier.prixTotalHT = this.montant;
        itemPanier.prixUnitaireTTC = this.form.paiement.tarifTTC;
        itemPanier.prixTotalTTC = this.montantTTC;

        if (this.modeEdition) {
          this.updateItemPanier(itemPanier);
          this.$router.go(-1);
        } else {
          this.ajouterAuPanier(itemPanier);
          this.$dialog.confirm({
            title: 'Article ajouté au panier',
            text: 'L\'article a bien été ajouté à votre panier',
            actions: {
              continuer: {
                text: 'Continuer mes achats',
                color: 'primary',
                flat: false,
                outlined: true
              },
              panier: {
                text: 'Voir mon panier',
                color: 'primary',
                flat: false
              }
            },
            showClose: false,
            persistent: true
          }).then(action => {
            if (action === 'panier') {
              this.$router.push({ name: RouteName.MAIN_PANIER });
            } else {
              this.$router.push({ name: RouteName.MAIN_DASHBOARD });
            }
          });
        }
      }
    },
    changeReglement () {
      this.loadingRechPrix = true;

      const codeReglement = this.form.paiement.reglement.codeReglement;
      this.dateEcheanceModifiable = codeReglement === 'R1';

      if (codeReglement !== 'R1') {
        this.loadingDateEcheance = true;
        TiersApi.getEcheanceTiers(this.infoTiers.numTiers, codeReglement, this.form.livraison.echeanceLivraison)
          .then((retourEcheance) => {
            this.form.paiement.echeancePaiement = retourEcheance.dateEcheance;
            this.loadingDateEcheance = false;
          })
          .then(this.calculPrix)
          .finally(() => {
            this.loadingDateEcheance = false;
          });
      } else {
        this.calculPrix();
      }
      this.loading = false;
    },
    changeEcheancePaiement () {
      if (!this.form.paiement.reglement) return;
      if (this.form.paiement.reglement.codeReglement === 'R1') {
        this.calculPrix();
      }
    },
    controleDate (date: string) {
      const _date = parseISO(date);
      const today = startOfDay(Date.now());
      let test1 = !(isBefore(_date, today));
      if (this.form.paiement.reglement.codeReglement === 'R1') {
        const day45j = addDays(today, 45);
        test1 = isWithinInterval(_date, { start: today, end: day45j });
      }
      return test1;
    },
    calculPrix () {
      this.loadingRechPrix = true;

      const ligneCommande = new LigneCommande();
      ligneCommande.articleLieuDepart = this.articlesLieuxDepart.find((ald: ArticleLieuDepart) => ald.article.id === this.idArticle);
      ligneCommande.qteLivr = this.form.quantite * this.conditionnement.facteurUniteBase;
      ligneCommande.quantiteUnitaire = this.form.quantite;
      ligneCommande.codeConditionnement = this.conditionnement.code;
      ligneCommande.dateDebutLivraison = this.form.livraison.echeanceLivraison;
      ligneCommande.adresseLivraison = this.form.livraison.adresse;

      const reglement = new ReglementCommande();
      reglement.dateEcheance = this.form.paiement.echeancePaiement;
      reglement.modeReglement = this.form.paiement.reglement.codeReglement;
      reglement.tauxEscompte = this.form.paiement.reglement.tauxEscompte;
      reglement.prixUnitaireHT = null;
      reglement.prixUnitaireTTC = null;
      reglement.montantTotalHT = null;
      reglement.montantTotalTTC = null;

      ligneCommande.reglement = reglement;

      const commande = new Commande(this.infoTiers.numTiers, null, [ligneCommande]);

      CommandeApi.valoriserCommande(commande).then(commandeValorisee => {
        this.form.paiement.tarif = commandeValorisee.lignes[0].reglement.prixUnitaireHT;
        this.form.paiement.tarifTTC = commandeValorisee.lignes[0].reglement.prixUnitaireTTC;
      }).catch(erreur => {
        if (erreur.response.status === 409 || erreur.response.status === 400) {
          this.form.paiement.tarif = null;
          this.form.paiement.tarifTTC = null;
          const messageErreurComplet = erreur.response.data.message;
          // const apresMessage = messageErreurComplet.split('message');
          // const apresException = apresMessage[1].split('exception');
          // const long = apresException[0].length;
          // const messageErreur = apresException[0].substr(3, (long - 6));

          let erreurTexte = '<b>Veuillez corriger votre commande</b><br>';
          erreurTexte = erreurTexte + (erreur.response.status === 409 || erreur.response.status === 400 ? messageErreurComplet : 'Une erreur interne est survenue.');

          this.$dialog.warning({
            text: erreurTexte + '<br/>',
            title: 'Commande invalide',
            actions: {
              panier: { text: 'Corriger ma commande', color: 'warning', flat: false }
            }
          });
        } else {
          throw erreur;
        }
      }).finally(() => {
        this.loadingRechPrix = false;
      });
    }
  }
});
